import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X } from 'lucide-react';
import { getCompanyInfo } from '../api/api';
import { useAppearance } from '../contexts/AppearanceContext';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [company, setCompany] = useState(null);
  const { appearance } = useAppearance();

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const data = await getCompanyInfo();
        setCompany(data);
      } catch (error) {
        console.error('Error fetching company info:', error);
      }
    };

    fetchCompany();

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Chaque entrée du menu mène à sa propre page dédiée : une adresse que le
  // visiteur peut partager, mettre en favori, et que Google indexe séparément.
  // Un clic ouvre donc toujours la route correspondante ; sur l'accueil, le
  // lien « Accueil » se contente de remonter en haut de page.
  const naviguer = (item) => {
    setIsMobileMenuOpen(false);

    if (item.path === '/' && pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    navigate(item.path);
  };

  const navItems = [
    { label: 'Accueil', id: 'hero', path: '/' },
    { label: 'Services', id: 'services', path: '/services' },
    { label: 'À propos', id: 'about', path: '/a-propos' },
    { label: 'Avis clients', id: 'avis', path: '/avis' },
    { label: 'Contact', id: 'contact', path: '/contact' }
  ];

  // Logo : image envoyée dans le panel (Apparence) si présente, sinon le logo
  // NETTO+ fourni. Alt renseigné pour l'accessibilité et le SEO.
  const logoSrc = appearance.logo_url || '/logo.png';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex items-center cursor-pointer" onClick={() => naviguer({ path: '/' })}>
            <img
              src={logoSrc}
              alt={`${company?.name || 'NETTO+'} — entreprise de nettoyage à Liège`}
              className="h-11 w-auto"
              width="176"
              height="44"
            />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => naviguer(item)}
                className="px-4 py-2 text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200 rounded-lg hover:bg-blue-50"
              >
                {item.label}
              </button>
            ))}
          </nav>

          {/* CTA Button Desktop */}
          <div className="hidden md:block">
            <Button
              onClick={() => {
                setIsMobileMenuOpen(false);
                navigate('/devis');
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-lg font-semibold shadow-lg shadow-blue-600/30 transition-all duration-300 hover:shadow-xl hover:shadow-blue-600/40"
            >
              Demander un devis gratuit
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Menu"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6 text-gray-700" />
            ) : (
              <Menu className="w-6 h-6 text-gray-700" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-100">
            <nav className="flex flex-col space-y-2">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => naviguer(item)}
                  className="px-4 py-3 text-left text-gray-700 hover:text-blue-600 hover:bg-blue-50 font-medium transition-colors duration-200 rounded-lg"
                >
                  {item.label}
                </button>
              ))}
              <Button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  navigate('/devis');
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold mt-2"
              >
                Demander un devis gratuit
              </Button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};
